/**
 * useDismissable — the two ways a popover closes.
 *
 * A disclosure (a menu, a notification panel) is not a dialog: it does not trap
 * focus, because a user must be able to tab past it and carry on. What it does
 * owe is the pair everyone expects — Escape closes it and returns focus to the
 * control that opened it, and a click anywhere outside dismisses it.
 *
 * That pair was written out longhand in every popover in the portal. This is
 * the same behaviour in one place. For a modal dialog, use `useFocusTrap`
 * instead, which includes this and adds the focus cycle.
 */
import { useEffect, useRef, type RefObject } from 'react';

export function useDismissable(
  /** Wraps both the trigger and the panel, so a click on either is "inside". */
  containerRef: RefObject<HTMLElement | null>,
  open: boolean,
  onDismiss: () => void,
  /** Focused when Escape closes it — the button that opened the panel. */
  triggerRef?: RefObject<HTMLElement | null>,
) {
  // Read handlers through refs so an inline arrow does not re-subscribe on
  // every render.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      onDismissRef.current();
      // Escape is a deliberate exit, so focus goes back where it came from
      // rather than being left on a panel that no longer exists.
      triggerRef?.current?.focus();
    };
    // pointerdown rather than click: a popover should be gone by the time the
    // press lands on whatever is underneath it.
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) onDismissRef.current();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [containerRef, open, triggerRef]);
}
